/**
 * CreditDetailPage
 * Shows full credit detail with user info, financial breakdown, and amortization schedule
 * Route: /credit/detail/:creditId?directoryId=xxx
 */

import { useParams, useSearchParams, Link } from 'react-router-dom';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { AmortizationTable } from '@/features/credit/components/AmortizationTable';
import { EarlyPaymentActions } from '@/features/credit/components/EarlyPaymentActions';
import { EarlyPaymentModal } from '@/features/credit/components/EarlyPaymentModal';
import { PaymentHistoryTable } from '@/features/credit/components/PaymentHistoryTable';
import { CreditStatementsTable } from '@/features/credit/components/CreditStatementsTable';
import { CreditStatementViewerModal } from '@/features/credit/components/CreditStatementViewerModal';
import { SignedContractsTable } from '@/features/credit/components/SignedContractsTable';
import { SignedContractViewerModal } from '@/features/credit/components/SignedContractViewerModal';
import { FeeBreakdown } from '@/features/credit/components/FeeBreakdown';
import { useCreditDetail, useEarlyPayment, useCreditPayments, useCreditStatements, useSignedContracts } from '@/features/credit/hooks';
import { useCreditLien } from '@/features/collection/hooks';
import { LienSummaryCard } from '@/features/collection/components';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';

import { CreditLevel } from '@/types/credit.types';

// Sección lien embed: visible sólo cuando credit.status === DEFAULTED.
function CreditLienSection({ creditId }: { creditId: string }) {
  const { lien, collections, isLoading } = useCreditLien(creditId);
  if (isLoading) return <Card><Skeleton className="h-32 w-full" /></Card>;
  if (!lien) {
    return (
      <Card title="Cobro Latente" description="Estado del WalletLien">
        <p className="text-sm text-muted-foreground">
          Este credito DEFAULTED aun no tiene un WalletLien creado.
        </p>
      </Card>
    );
  }
  return <LienSummaryCard lien={lien} collections={collections} />;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'DELINQUENT':
      return 'warning';
    case 'DEFAULT':
      return 'danger';
    case 'PAID_OFF':
      return 'info';
    case 'SETTLED':
      return 'info';
    default:
      return 'default';
  }
}

// ─── Info Row Helper ──────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums text-foreground">{value ?? '-'}</span>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={`card-skeleton-${i.toString()}`} className="rounded-lg border border-border bg-card p-5">
            <Skeleton variant="text" width="50%" height={16} />
            <div className="mt-4 space-y-3">
              {Array.from({ length: 4 }).map((_, j) => (
                <Skeleton key={`row-${j.toString()}`} variant="text" height={14} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border bg-card p-6">
        <Skeleton variant="text" width="30%" height={18} />
        <div className="mt-4">
          <Skeleton variant="rectangular" height={200} />
        </div>
      </div>
    </div>
  );
}

// ─── Error State ──────────────────────────────────────────────────────────────

function DetailError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar el detalle del credito
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Breadcrumb Builder ───────────────────────────────────────────────────────

function buildBreadcrumbs(from: string, creditId: string) {
  const last = { label: `Credito #${creditId.slice(0, 8)}`, path: '#' };
  switch (from) {
    case 'cobros':
      return [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
        { label: 'Por Cobrar', path: ROUTES.COLLECTION_DASHBOARD },
        last,
      ];
    case 'cobrados':
      return [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Cobranza', path: ROUTES.COLLECTION_COBRADOS },
        { label: 'Cobrados', path: ROUTES.COLLECTION_COBRADOS },
        last,
      ];
    case 'cartera':
      return [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
        { label: 'Cartera', path: ROUTES.COLLECTION_CARTERA },
        last,
      ];
    default:
      return [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Credito', path: ROUTES.ACTIVE_CREDITS },
        { label: 'Activos', path: ROUTES.ACTIVE_CREDITS },
        last,
      ];
  }
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function CreditDetailPage() {
  const { creditId = '' } = useParams<{ creditId: string }>();
  const [searchParams] = useSearchParams();
  const directoryId = searchParams.get('directoryId') ?? '';

  const { credit, schedule, user, isLoading, isError, refetch } = useCreditDetail(
    creditId,
    directoryId,
  );

  const earlyPayment = useEarlyPayment(creditId, directoryId);
  const payments = useCreditPayments(creditId);
  const statements = useCreditStatements(creditId);
  const signedContracts = useSignedContracts(directoryId);

  // Compute outstanding balance from schedule (sum of unpaid amounts)
  const computedOutstandingBalance = schedule
    ? schedule.reduce((sum, coupon) => {
        if (coupon.status !== 'PAID') {
          return sum + (coupon.total_mxn - coupon.paid_amount_mxn);
        }
        return sum;
      }, 0)
    : 0;

  const from = searchParams.get('from') ?? '';
  const breadcrumbs = buildBreadcrumbs(from, creditId);

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={`Detalle de Credito #${creditId.slice(0, 8)}`}
          breadcrumbs={breadcrumbs}
        />
        <DetailSkeleton />
      </div>
    );
  }

  if (isError || !credit) {
    return (
      <div>
        <PageHeader
          title={`Detalle de Credito #${creditId.slice(0, 8)}`}
          breadcrumbs={breadcrumbs}
        />
        <DetailError onRetry={() => refetch()} />
      </div>
    );
  }

  const levelIsValid = Object.values(CreditLevel).includes(credit.credit_level as CreditLevel);

  return (
    <div>
      <PageHeader
        title={`Detalle de Credito #${credit.credit_id.slice(0, 8)}`}
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2">
            
            <EarlyPaymentActions
              creditStatus={credit.status}
              outstandingBalance={computedOutstandingBalance}
              onSelectType={earlyPayment.openModal}
            />
            <Button onClick={() => refetch()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {/* Row 1: Info cards */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Card: Usuario */}
          <Card title="Usuario">
            <div className="space-y-1">
              {user && (
                <>
                  <InfoRow label="Nombre" value={user.full_name || '—'} />
                  <InfoRow label="Telefono" value={user.phone_number} />
                  <InfoRow label="Directory ID" value={user.directory_id} />
                  <InfoRow label="People ID" value={user.people_id} />
                  <div className="mt-3">
                    <Link
                      to={`/credit/profiles/${user.directory_id}?from=cobranza`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Ver Perfil Financiero
                    </Link>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Card: Credito */}
          <Card title="Credito">
            <div className="space-y-1">
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">Estado</span>
                <Badge variant={getStatusVariant(credit.status)}>{credit.status}</Badge>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">Nivel</span>
                {levelIsValid ? (
                  <CreditLevelBadge level={credit.credit_level as CreditLevel} size="sm" />
                ) : (
                  <span className="text-sm text-muted-foreground">{credit.credit_level}</span>
                )}
              </div>
              <FeeBreakdown
                requestedAmountMxn={credit.amount_mxn}
                openingFeeMxn={credit.opening_fee_mxn}
                openingFeeIvaMxn={credit.opening_fee_iva_mxn}
                openingFeePct={credit.opening_fee_pct}
                financedAmountMxn={credit.financed_amount_mxn}
              />
              <InfoRow label="Plazo" value={`${credit.term_weeks} semanas`} />
              <InfoRow label="Tasa Anual" value={`${(credit.annual_rate * 100).toFixed(1)}%`} />
            </div>
          </Card>

          {/* Card: Financiero */}
          <Card title="Financiero">
            <div className="space-y-1">
              <InfoRow label="Pago Semanal" value={formatCurrency(credit.weekly_payment_mxn, 'MXN', 'es-MX')} />
              <InfoRow label="Interes Total" value={formatCurrency(credit.total_interest_mxn, 'MXN', 'es-MX')} />
              <InfoRow label="IVA Total" value={formatCurrency(credit.total_iva_mxn, 'MXN', 'es-MX')} />
              <InfoRow label="Total a Pagar" value={formatCurrency(credit.total_payable_mxn, 'MXN', 'es-MX')} />
              <div className="mt-2 border-t border-border pt-2">
                <p className="mb-1 text-xs font-medium text-muted-foreground">Split Fiado / UNE-Credito</p>
                <InfoRow label="Tasa Fiado" value={`${credit.fiado_rate_points.toFixed(1)}%`} />
                <InfoRow label="Tasa UNE-Credito" value={`${credit.partner_rate_points.toFixed(1)}%`} />
                <InfoRow label="Interes Fiado" value={formatCurrency(credit.fiado_interest_mxn, 'MXN', 'es-MX')} />
                <InfoRow label="Interes UNE-Credito" value={formatCurrency(credit.partner_interest_mxn, 'MXN', 'es-MX')} />
              </div>
            </div>
          </Card>
        </div>

        {/* Row 2: Amortization Table */}
        <Card title="Tabla de Amortizacion" description="Calendario de pagos del credito">
          <AmortizationTable schedule={schedule} />
        </Card>

        {/* Row 3: Payment History */}
        <Card title="Historial de Pagos" description="Movimientos y cobros registrados para este credito">
          <PaymentHistoryTable
            records={payments.records}
            isLoading={payments.isLoading}
          />
        </Card>

        {/* Row 4: Estados de Cuenta + Contratos Firmados (lado a lado) */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card
            title="Estados de Cuenta"
            description="PDFs mensuales generados conforme a la LTOSF (P7)"
          >
            <CreditStatementsTable
              statements={statements.statements}
              isLoading={statements.isLoading}
              isError={statements.isError}
              viewingId={statements.viewer.statementId}
              onView={statements.handleView}
              onRetry={() => statements.refetch()}
            />
          </Card>

          <Card
            title="Contratos Firmados"
            description="Documentos legales firmados por el usuario (contrato, carta de cesión, anexos, etc.)"
          >
            <SignedContractsTable
              contracts={signedContracts.contracts}
              isLoading={signedContracts.isLoading}
              isError={signedContracts.isError}
              viewingId={signedContracts.viewer.contractId}
              onView={signedContracts.handleView}
              onRetry={() => signedContracts.refetch()}
            />
          </Card>

          {credit.status === 'DEFAULTED' && (
            <CreditLienSection creditId={credit.credit_id} />
          )}
        </div>
      </div>

      <CreditStatementViewerModal
        isOpen={statements.viewer.isOpen}
        onClose={statements.closeViewer}
        period={statements.viewer.period}
        url={statements.viewer.url}
        isLoading={statements.viewer.isLoading}
      />

      <SignedContractViewerModal
        isOpen={signedContracts.viewer.isOpen}
        onClose={signedContracts.closeViewer}
        title={signedContracts.viewer.title}
        url={signedContracts.viewer.url}
        isLoading={signedContracts.viewer.isLoading}
      />

      <EarlyPaymentModal
        isOpen={earlyPayment.isModalOpen}
        onClose={earlyPayment.closeModal}
        paymentType={earlyPayment.paymentType}
        creditId={creditId}
        outstandingBalance={computedOutstandingBalance}
        weeklyPayment={credit.weekly_payment_mxn}
        onSubmit={earlyPayment.submitPayment}
        isPending={earlyPayment.isPending}
        isSuccess={earlyPayment.isSuccess}
        lastResult={earlyPayment.lastResult}
        settlementQuote={earlyPayment.settlementQuote}
        isLoadingQuote={earlyPayment.isLoadingQuote}
      />
    </div>
  );
}
